import { MasterInstance } from "@/instance/axios";
import { PartnersGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { InfiniteData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

export type PartnerOption = {
  value: string;
  label: string;
  group: string;
};

const getAutoCompleteKeyword = (keyword: string, partnerType: string) =>
  MasterInstance.partnersFindPost(
    {
      search: keyword,
      searchFields: ["code", "name"],
    },
    {
      params: {
        query: {
          ...(partnerType && {
            type: { $eq: partnerType },
          }),
        },
      },
    },
  ).then((res: AxiosResponse<PartnersGet200ResponseRowsInner[]>) => res.data);

export const usePartnersAutoComplete = (
  focus = false,
  keyword = "",
  partnerType?: string,
  options?: {
    onSuccess: (data: PartnerOption[]) => void;
  },
) => {
  return useQuery(
    ["partners-get-autocomplete", keyword, partnerType],
    () => getAutoCompleteKeyword(keyword, partnerType ?? ""),
    {
      enabled: focus,
      select: (data: PartnersGet200ResponseRowsInner[]) =>
        data?.map((partner) => {
          return {
            value: String(partner.code),
            label: String(partner.name),
            group: String(partner.type),
          };
        }) ?? [],
      onSuccess: options?.onSuccess,
    },
  );
};

// API 호출 함수 수정: pageParam 추가
const getAutoCompleteKeywords = (
  keyword: string,
  partnerType?: string,
  pageParam: number = 1,
  pageSize: number = 20, // 페이지 크기 정의
) =>
  MasterInstance.partnersFindPost(
    {
      search: keyword,
      searchFields: ["code", "name"],
    },
    {
      params: {
        query: {
          ...(partnerType && {
            type: { $eq: partnerType },
          }),
        },
        limit: pageSize,
        offset: (pageParam - 1) * pageSize,
      },
    },
  ).then((res: AxiosResponse<PartnersGet200ResponseRowsInner[]>) => res.data);

// InfiniteQuery 훅
export const useAutoCompleteInfinite = (
  keyword: string,
  partnerType?: string,
  pageParam: number = 1,
  options?: {
    onSuccess: (data?: PartnerOption[]) => void;
  },
) =>
  useInfiniteQuery(
    ["autocomplete-partners", keyword, partnerType, pageParam],
    ({ pageParam = 1 }) => getAutoCompleteKeywords(keyword, partnerType, pageParam),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < 20) {
          return undefined; // 더 이상 데이터 없음
        }
        return allPages.length + 1; // 다음 페이지 번호 반환
      },
      select: (data: InfiniteData<PartnersGet200ResponseRowsInner[]>) => {
        return {
          ...data,
          pages: data.pages.map((page) =>
            page.map((partner) => ({
              value: String(partner.code),
              label: String(partner.name),
              group: String(partner.type),
            })),
          ),
        };
      },
    },
  );

const getPartnersByCode = (code: string | null) =>
  MasterInstance.partnersFindPost(
    {
      ...(code && { search: code }),
      searchFields: ["code"],
    },
    {
      params: {
        query: {},
      },
    },
  ).then((res: AxiosResponse<PartnersGet200ResponseRowsInner[]>) => res.data);

export const usePartnersCode = (
  enabled: boolean,
  code: string | null,
  options?: {
    onSuccess: (data?: PartnerOption[]) => void;
  },
) => {
  return useQuery(["partners-get-code", code], () => getPartnersByCode(code), {
    enabled: enabled ?? false,
    select: (data: PartnersGet200ResponseRowsInner[]) =>
      data?.map((partner) => {
        return {
          value: String(partner.code),
          label: String(partner.name),
          group: String(partner.type),
        };
      }),
    onSuccess: options?.onSuccess,
  });
};
